<script lang="ts">
  /* ModuleGate — a paid door of the subscription constructor.

     mode="lock" (default): without the feature the content is replaced by a
       lock screen — what the module does and a button to the constructor
       with that module ticked. Accounting, reports, partner search and the
       Dashboard's management tabs use it.
     mode="banner": the content stays (its data is the company's own and
       stays readable) under a strip saying the module is not in the
       subscription. Warehouse pages and the public page editor use it.

     In banner mode the gate may be used self-closing, as a strip on top of
     the page: `<ModuleGate tenantSlug={slug} feature="warehouse" mode="banner" />`.

     Only the owner changes the subscription; anyone else is told to ask them. */
  import type { Snippet } from "svelte";
  import { _ } from "svelte-i18n";
  import { auth } from "$lib/auth";
  import { hasFeature, membershipFor, type Feature } from "$lib/permissions";
  import {
    FEATURE_CODE, ITEM_DESC_KEY, ITEM_NAME_KEY, constructorHref, markGateShown,
    type ItemCode, type PaidFeature,
  } from "$lib/billing";

  let {
    tenantSlug,
    feature,
    mode = "lock",
    children,
  }: { tenantSlug: string; feature: Feature; mode?: "lock" | "banner"; children?: Snippet } = $props();

  const allowed = $derived(hasFeature($auth, tenantSlug, feature));
  const code = $derived<ItemCode>(FEATURE_CODE[feature as PaidFeature] ?? "base");
  const isBase = $derived(code === "base");
  const owner = $derived(membershipFor($auth, tenantSlug)?.role === "owner");
  const href = $derived(constructorHref(tenantSlug, code));

  // The page's own loads answer 402 while the lock is up; the lock already
  // says it all, so the global upgrade dialog stays shut for this feature.
  $effect(() => {
    if (mode !== "lock" || allowed) return;
    return markGateShown(feature);
  });
</script>

{#if allowed}
  {@render children?.()}
{:else if mode === "banner"}
  <div class="mg-banner" role="status">
    <span class="material-icons-outlined mg-banner-icon" aria-hidden="true">lock</span>
    <div class="mg-banner-text">
      <strong>{$_("billing.gate.bannerTitle", { values: { module: $_(ITEM_NAME_KEY[code]) } })}</strong>
      <span>{owner ? $_("billing.gate.bannerBody") : $_("billing.gate.askOwner")}</span>
    </div>
    {#if owner}
      <a class="btn btn--primary mg-btn" href={href}>{$_("billing.gate.cta")}</a>
    {/if}
  </div>
  {@render children?.()}
{:else}
  <div class="mg-lock">
    <span class="material-icons-outlined mg-lock-icon" aria-hidden="true">lock</span>
    <h2 class="mg-title">
      {isBase ? $_("upgrade.locked.pricing.title") : $_(ITEM_NAME_KEY[code])}
    </h2>
    <p class="mg-what">{$_(ITEM_DESC_KEY[code])}</p>
    <p class="mg-body">{isBase ? $_("billing.gate.baseBody") : $_("billing.gate.lockBody")}</p>
    {#if owner}
      <a class="btn btn--primary mg-btn" href={href}>{$_("billing.gate.cta")}</a>
    {:else}
      <p class="mg-note">{$_("billing.gate.askOwner")}</p>
    {/if}
  </div>
{/if}

<style>
  .mg-banner {
    display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
    background: var(--color-warning-soft); border: 1px solid #f3d6a6; border-radius: var(--radius);
    padding: 10px 14px; margin-bottom: 14px;
  }
  .mg-banner-icon { color: #b7791f; font-size: 22px; flex-shrink: 0; }
  .mg-banner-text { display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 200px; font-size: 13px; line-height: 1.4; }
  .mg-banner-text strong { color: var(--color-surface-darker); font-size: 13.5px; }
  .mg-banner-text span { color: var(--color-text-muted); }

  .mg-lock {
    display: flex; flex-direction: column; align-items: center; text-align: center; gap: 10px;
    max-width: 480px; margin: 48px auto; padding: 32px 24px;
    background: #fff; border: 1px solid var(--color-divider); border-radius: 12px;
  }
  .mg-lock-icon {
    font-size: 26px; color: var(--color-primary); background: var(--color-primary-soft);
    border-radius: 50%; width: 48px; height: 48px; display: inline-flex; align-items: center; justify-content: center;
  }
  .mg-title { margin: 4px 0 0; font-size: 19px; font-weight: 700; color: var(--color-surface-darker); }
  .mg-what { margin: 0; font-size: 14px; color: var(--color-text); line-height: 1.5; }
  .mg-body { margin: 0; font-size: 13px; color: var(--color-text-muted); line-height: 1.5; }
  .mg-note { margin: 4px 0 0; font-size: 13px; color: var(--color-text-muted); }
  .mg-btn { text-decoration: none; white-space: nowrap; margin-top: 4px; }
  .mg-btn:hover { text-decoration: none; }
</style>
